import { useEffect, useMemo, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { CalendarPlus, ChevronLeft, ChevronRight, Clock, ExternalLink, MapPin, Search, UserRound, X } from 'lucide-react';
import LiveScheduleEmbed, { OFFICIAL_SCHEDULE_URL } from './LiveScheduleEmbed';
import { publicHref } from '../lib/publicUrls';
import {
  CLASS_GROUPS, GROUP_BY_ID, TIMES_OF_DAY, addDays, calendarFile, clubNow, dayLabel, fetchWeek, liveStatus, weekOf,
} from '../lib/classSchedule';
import './ClassSchedule.css';

// The site's own class schedule (owner direction, Sept 30): a week strip, program filters, the day's
// classes by time of day, and a detail panel for each class. It shows the provider's live data; if
// that can't load, the provider's own calendar appears instead so the schedule never disappears.

const DIRECTIONS = 'https://www.google.com/maps/search/?api=1&query=Delta+Valley+Health+Club+120+Guthrie+Ln+Brentwood+CA';
const STATUS_LABEL = { now: 'Happening now', soon: 'Starting soon', ended: 'Ended' };

const duration = (item) => `${item.end - item.start} min`;
const spotsLabel = (spots) => (spots === 0 ? 'Full' : spots !== null && spots <= 5 ? `${spots} ${spots === 1 ? 'spot' : 'spots'} left` : null);

export default function ClassSchedule({ focus = 'all', categoryId, title = 'Delta Valley Health Club weekly class schedule' }) {
  const [now, setNow] = useState(() => clubNow());
  const [selected, setSelected] = useState(now.iso);
  const [group, setGroup] = useState(focus);
  const [timeOfDay, setTimeOfDay] = useState('any');
  const [query, setQuery] = useState('');
  const [week, setWeek] = useState({ status: 'loading', days: {} });
  const [open, setOpen] = useState(null);
  const [showEarlier, setShowEarlier] = useState(false);
  const listRef = useRef(null);
  const chipsRef = useRef(null);
  const days = useMemo(() => weekOf(selected), [selected]);

  useEffect(() => {
    const timer = setInterval(() => setNow(clubNow()), 60000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    let current = true;
    setWeek((prev) => ({ status: 'loading', days: prev.days }));
    fetchWeek(days)
      .then((loaded) => { if (current) setWeek({ status: 'ready', days: loaded }); })
      .catch(() => { if (current) setWeek({ status: 'error', days: {} }); });
    return () => { current = false; };
  }, [days]);

  // On phones the program chips scroll sideways; keep the chosen one in view.
  useEffect(() => {
    const row = chipsRef.current;
    const chip = row?.querySelector('[aria-pressed="true"]');
    if (row && chip && row.scrollWidth > row.clientWidth) row.scrollLeft = Math.max(0, chip.offsetLeft - row.offsetLeft - 16);
  }, [group, week.status]);

  const matches = (item) => {
    if (group !== 'all' && item.group !== group) return false;
    const band = TIMES_OF_DAY.find((entry) => entry.id === timeOfDay);
    if (band && (item.start < band.from || item.start >= band.to)) return false;
    const search = query.trim().toLowerCase();
    return !search || `${item.name} ${item.instructors.join(' ')} ${item.room}`.toLowerCase().includes(search);
  };
  const allDayClasses = (week.days[selected] || []).filter(matches);
  // Today, classes that have already ended fold away behind one button so what's next comes first.
  const earlier = allDayClasses.filter((item) => liveStatus(item, now) === 'ended');
  const dayClasses = showEarlier ? allDayClasses : allDayClasses.filter((item) => !earlier.includes(item));
  const bands = TIMES_OF_DAY.map((band) => ({ ...band, items: dayClasses.filter((item) => item.start >= band.from && item.start < band.to) })).filter((band) => band.items.length);
  const filtered = group !== 'all' || timeOfDay !== 'any' || query.trim();
  const presentGroups = CLASS_GROUPS.filter(({ id }) => id !== 'class' || Object.values(week.days).some((list) => list.some((item) => item.group === 'class')));

  const pickDay = (iso) => { setSelected(iso); setShowEarlier(false); listRef.current?.scrollTo?.({ top: 0 }); };
  const resetFilters = () => { setGroup('all'); setTimeOfDay('any'); setQuery(''); };

  if (week.status === 'error') {
    return (
      <div className="dv-sched dv-sched--fallback" data-testid="class-schedule">
        <p className="dv-size-body dv-sched__fallback-note" role="status">The live class list didn’t load just now, so here is the official calendar.</p>
        <LiveScheduleEmbed categoryId={categoryId} title={title} />
      </div>
    );
  }

  return (
    <div className="dv-sched" data-testid="class-schedule" aria-label={title} role="region">
      <div className="dv-sched__bar">
        <button type="button" className="dv-sched__step" onClick={() => pickDay(addDays(selected, -7))}>
          <ChevronLeft aria-hidden="true" /> <span>Previous week</span>
        </button>
        <p className="dv-sched__range" aria-live="polite">
          {dayLabel(days[0], { month: 'short', day: 'numeric' })} – {dayLabel(days[6], { month: 'short', day: 'numeric', year: 'numeric' })}
        </p>
        <div className="dv-sched__bar-end">
          {!days.includes(now.iso) && <button type="button" className="dv-sched__today" onClick={() => pickDay(now.iso)}>Today</button>}
          <button type="button" className="dv-sched__step" onClick={() => pickDay(addDays(selected, 7))}>
            <span>Next week</span> <ChevronRight aria-hidden="true" />
          </button>
        </div>
      </div>

      <div className="dv-sched__days" role="tablist" aria-label="Choose a day">
        {days.map((iso) => {
          const count = (week.days[iso] || []).filter(matches).length;
          const isToday = iso === now.iso;
          return (
            <button key={iso} type="button" role="tab" aria-selected={iso === selected} className="dv-sched__day" onClick={() => pickDay(iso)}
              aria-label={`${dayLabel(iso, { weekday: 'long', month: 'long', day: 'numeric' })}${isToday ? ', today' : ''}, ${week.status === 'ready' ? `${count} ${count === 1 ? 'class' : 'classes'}` : 'loading'}`}>
              <span className="dv-sched__day-name">{isToday ? 'Today' : dayLabel(iso, { weekday: 'short' })}</span>
              <span className="dv-sched__day-date">{dayLabel(iso, { day: 'numeric' })}</span>
              <span className="dv-sched__day-count">{week.status === 'ready' ? `${count} ${count === 1 ? 'class' : 'classes'}` : ' '}</span>
            </button>
          );
        })}
      </div>

      <div className="dv-sched__filters">
        <div className="dv-sched__chips" role="group" aria-label="Filter by program" ref={chipsRef}>
          <button type="button" className="dv-sched__chip" aria-pressed={group === 'all'} onClick={() => setGroup('all')}>All classes</button>
          {presentGroups.map(({ id, label, rgb }) => (
            <button key={id} type="button" className="dv-sched__chip" aria-pressed={group === id} style={{ '--sched-rgb': rgb }} onClick={() => setGroup(group === id ? 'all' : id)}>
              <span className="dv-sched__dot" aria-hidden="true" />{label}
            </button>
          ))}
        </div>
        <div className="dv-sched__refine">
          <div className="dv-sched__segments" role="group" aria-label="Time of day">
            {[{ id: 'any', label: 'Any time' }, ...TIMES_OF_DAY].map(({ id, label }) => (
              <button key={id} type="button" aria-pressed={timeOfDay === id} onClick={() => setTimeOfDay(id)}>{label}</button>
            ))}
          </div>
          <label className="dv-sched__search">
            <Search aria-hidden="true" />
            <span className="sr-only">Search classes, instructors or rooms</span>
            <input type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search classes or instructors" />
          </label>
        </div>
      </div>

      <div className="dv-sched__list" ref={listRef} aria-busy={week.status === 'loading'}>
        <h3 className="sr-only">{dayLabel(selected, { weekday: 'long', month: 'long', day: 'numeric' })}</h3>
        {week.status === 'loading' ? (
          <ul className="dv-sched__skeleton" aria-label="Loading classes">{[0, 1, 2, 3].map((key) => <li key={key} />)}</ul>
        ) : (earlier.length > 0 || bands.length) ? <>
          {earlier.length > 0 && (
            <button type="button" className="dv-sched__earlier" aria-expanded={showEarlier} onClick={() => setShowEarlier(!showEarlier)}>
              {showEarlier ? 'Hide earlier classes' : `Show ${earlier.length} earlier ${earlier.length === 1 ? 'class' : 'classes'} from today`}
            </button>
          )}
          {bands.map((band) => (
          <section key={band.id} className="dv-sched__band" aria-label={`${band.label} classes`}>
            <p className="dv-sched__band-title">{band.label}<span>{band.items.length} {band.items.length === 1 ? 'class' : 'classes'}</span></p>
            <ul>
              {band.items.map((item) => {
                const status = liveStatus(item, now);
                const spots = spotsLabel(item.spots);
                const info = GROUP_BY_ID[item.group];
                return (
                  <li key={item.id}>
                    <button type="button" className={`dv-sched__class${status ? ` is-${status}` : ''}`} style={{ '--sched-rgb': info.rgb }} onClick={() => setOpen(item)}>
                      <span className="dv-sched__time"><strong>{item.startLabel}</strong><span>{duration(item)}</span></span>
                      <span className="dv-sched__rule" aria-hidden="true" />
                      <span className="dv-sched__main">
                        <span className="dv-sched__name">{item.name}</span>
                        <span className="dv-sched__meta">{[item.instructors.join(', '), item.room].filter(Boolean).join(' · ') || item.location}</span>
                      </span>
                      <span className="dv-sched__tags">
                        {status && status !== 'ended' && <span className={`dv-sched__tag dv-sched__tag--${status}`}>{STATUS_LABEL[status]}</span>}
                        <span className="dv-sched__tag dv-sched__tag--group">{info.label}</span>
                        {item.level && <span className="dv-sched__tag">{item.level}</span>}
                        {item.extraFee && <span className="dv-sched__tag">Extra fee</span>}
                        {spots && <span className={`dv-sched__tag${item.spots === 0 ? ' dv-sched__tag--full' : ''}`}>{spots}</span>}
                      </span>
                      <ChevronRight className="dv-sched__chevron" aria-hidden="true" />
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>
          ))}
          {!bands.length && <p className="dv-size-body dv-sched__done">That’s it for today’s classes. Tomorrow’s schedule is one tap away.</p>}
        </> : (
          <div className="dv-sched__empty">
            <p className="dv-size-body">{filtered ? 'No classes match these filters on this day.' : 'No classes are scheduled on this day.'}</p>
            {filtered && <button type="button" className="dv-button dv-button--outline" onClick={resetFilters}>Show All Classes</button>}
          </div>
        )}
      </div>

      <p className="dv-sched__source">
        Times update live from Delta Valley’s scheduling system.{' '}
        <a href={OFFICIAL_SCHEDULE_URL} target="_blank" rel="noopener noreferrer">Open the official schedule <ExternalLink aria-hidden="true" /></a>
      </p>

      <ClassDetail item={open} now={now} onClose={() => setOpen(null)} />
    </div>
  );
}

function ClassDetail({ item, now, onClose }) {
  const addToCalendar = () => {
    const url = URL.createObjectURL(new Blob([calendarFile(item)], { type: 'text/calendar;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `${item.name.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-${item.date}.ics`;
    document.body.append(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const info = item && GROUP_BY_ID[item.group];
  const status = item && liveStatus(item, now);
  const spots = item && spotsLabel(item.spots);
  return (
    <Dialog.Root open={Boolean(item)} onOpenChange={(value) => { if (!value) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="dv-sched-detail__overlay" />
        <Dialog.Content className="dv-sched-detail" style={item ? { '--sched-rgb': info.rgb } : undefined} aria-describedby={undefined}>
          {item && (
            <>
              <div className="dv-sched-detail__band">
                <p className="dv-sched-detail__eyebrow">{info.label}{status ? ` · ${STATUS_LABEL[status]}` : ''}</p>
                <Dialog.Close className="dv-sched-detail__close"><X aria-hidden="true" /><span className="sr-only">Close</span></Dialog.Close>
              </div>
              <div className="dv-sched-detail__body">
                <Dialog.Title className="dv-size-subheading dv-sched-detail__title">{item.name}</Dialog.Title>
                <dl className="dv-sched-detail__facts">
                  <div><dt><Clock aria-hidden="true" /> When</dt><dd>{dayLabel(item.date, { weekday: 'long', month: 'long', day: 'numeric' })}<br />{item.startLabel} – {item.endLabel} · {duration(item)}</dd></div>
                  {item.instructors.length > 0 && <div><dt><UserRound aria-hidden="true" /> Instructor</dt><dd>{item.instructors.join(', ')}</dd></div>}
                  <div><dt><MapPin aria-hidden="true" /> Where</dt><dd>{[item.room, `${item.location} club`].filter(Boolean).join(' · ')}</dd></div>
                </dl>
                {(item.level || item.extraFee || spots) && (
                  <p className="dv-sched-detail__tags">
                    {item.level && <span className="dv-sched__tag">{item.level}</span>}
                    {item.extraFee && <span className="dv-sched__tag">Extra fee</span>}
                    {spots && <span className={`dv-sched__tag${item.spots === 0 ? ' dv-sched__tag--full' : ''}`}>{spots}</span>}
                  </p>
                )}
                {item.description && <p className="dv-size-body dv-sched-detail__description">{item.description}</p>}
                <div className="dv-sched-detail__actions">
                  {item.bookable
                    ? <a className="dv-button dv-sched-detail__primary" href={OFFICIAL_SCHEDULE_URL} target="_blank" rel="noopener noreferrer">Reserve a Spot <ExternalLink aria-hidden="true" /></a>
                    : <a className="dv-button dv-sched-detail__primary" href={OFFICIAL_SCHEDULE_URL} target="_blank" rel="noopener noreferrer">View in Official Schedule <ExternalLink aria-hidden="true" /></a>}
                  <button type="button" className="dv-button dv-button--outline" onClick={addToCalendar}><CalendarPlus aria-hidden="true" /> Add to My Calendar</button>
                  <a className="dv-button dv-button--outline" href={DIRECTIONS} target="_blank" rel="noopener noreferrer"><MapPin aria-hidden="true" /> Directions</a>
                </div>
                <p className="dv-sched-detail__note">Not a member yet? <a href={publicHref('/book-a-tour')}>Book a tour</a> of the club.</p>
              </div>
            </>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
